import {
  AfterViewInit,
  Component,
  ComponentRef,
  DestroyRef,
  EnvironmentInjector,
  NgModuleRef,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  createNgModule,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FloatingNotesStateService } from '../shared/collaboration/floating-notes-state.service';
import { SidebarService } from '../shared/collaboration/sidebar.service';
import type { SidebarContainerComponent } from '../shared/overlays/sidebar-container/sidebar-container.component';

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

@Component({
  selector: 'app-lazy-shell-overlays',
  standalone: false,
  template: `
    @if (loading()) {
      <div class="lazy-shell-status" role="status" aria-live="polite">
        Cargando paneles opcionales...
      </div>
    }
    @if (loadError()) {
      <div class="lazy-shell-status lazy-shell-status--error" role="alert">
        <span>No se pudieron cargar los paneles opcionales.</span>
        <button type="button" (click)="retry()">Reintentar</button>
      </div>
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    .lazy-shell-status {
      align-items: center;
      background: var(--surface-card);
      border: 1px solid var(--border-control);
      color: var(--text-primary);
      border-radius: 0.5rem;
      bottom: 1rem;
      display: flex;
      gap: 0.75rem;
      left: 1rem;
      padding: 0.75rem 1rem;
      position: fixed;
      z-index: 1500;
    }

    .lazy-shell-status--error {
      border-color: var(--status-danger-fg);
    }
  `],
})
export class LazyShellOverlaysComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sidebarService = inject(SidebarService);
  private readonly floatingNotesService = inject(FloatingNotesStateService);
  private notesModuleRef: NgModuleRef<unknown> | null = null;
  private sidebarModuleRef: NgModuleRef<unknown> | null = null;
  private sidebarComponentRef: ComponentRef<SidebarContainerComponent> | null = null;
  private notesLoadPromise: Promise<void> | null = null;
  private sidebarLoadPromise: Promise<void> | null = null;
  private pendingLoads = 0;
  private lastLoadError: 'notes' | 'sidebar' | null = null;
  private idleHandle: number | null = null;

  readonly loading = signal(false);
  readonly loadError = signal(false);

  ngOnInit(): void {
    this.sidebarService.overlayLoadRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.loadSidebarUi());

    this.sidebarService.agendaOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOpen) => {
        if (isOpen) {
          void this.loadSidebarUi();
        }
      });

    this.sidebarService.tasksOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOpen) => {
        if (isOpen) {
          void this.loadSidebarUi();
        }
      });

    this.floatingNotesService.widgetLoadRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.loadNotesUi());
  }

  ngAfterViewInit(): void {
    // Notes were visible before the migration, so keep them ready after the
    // first render without putting their implementation in the initial graph.
    if (this.floatingNotesService.visible) {
      this.scheduleIdleLoad();
    }
  }

  ngOnDestroy(): void {
    this.cancelIdleLoad();
    this.notesModuleRef?.destroy();
    this.sidebarModuleRef?.destroy();
    this.notesModuleRef = null;
    this.sidebarModuleRef = null;
    this.sidebarComponentRef = null;
  }

  retry(): void {
    if (this.lastLoadError === 'sidebar') {
      void this.loadSidebarUi();
      return;
    }
    void this.loadNotesUi();
  }

  private scheduleIdleLoad(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const idleWindow = window as IdleWindow;
    if (idleWindow.requestIdleCallback) {
      this.idleHandle = idleWindow.requestIdleCallback(
        () => void this.loadNotesUi(),
        { timeout: 3000 },
      );
      return;
    }

    this.idleHandle = window.setTimeout(() => void this.loadNotesUi(), 2000);
  }

  private cancelIdleLoad(): void {
    if (this.idleHandle === null || typeof window === 'undefined') {
      return;
    }

    const idleWindow = window as IdleWindow;
    if (idleWindow.cancelIdleCallback) {
      idleWindow.cancelIdleCallback(this.idleHandle);
    } else {
      window.clearTimeout(this.idleHandle);
    }
    this.idleHandle = null;
  }

  private loadNotesUi(): Promise<void> {
    if (this.notesModuleRef) {
      return Promise.resolve();
    }
    if (this.notesLoadPromise) {
      return this.notesLoadPromise;
    }

    this.cancelIdleLoad();
    this.beginLoad();
    this.loadError.set(false);
    this.lastLoadError = null;
    this.notesLoadPromise = this.loadNotesUiInternal().finally(() => {
      this.endLoad();
      this.notesLoadPromise = null;
    });
    return this.notesLoadPromise;
  }

  private loadSidebarUi(): Promise<void> {
    if (this.sidebarModuleRef) {
      return Promise.resolve();
    }
    if (this.sidebarLoadPromise) {
      return this.sidebarLoadPromise;
    }

    this.beginLoad();
    this.loadError.set(false);
    this.lastLoadError = null;
    this.sidebarLoadPromise = this.loadSidebarUiInternal().finally(() => {
      this.endLoad();
      this.sidebarLoadPromise = null;
    });
    return this.sidebarLoadPromise;
  }

  private beginLoad(): void {
    this.pendingLoads += 1;
    this.loading.set(true);
  }

  private endLoad(): void {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
    this.loading.set(this.pendingLoads > 0);
  }

  private async loadNotesUiInternal(): Promise<void> {
    try {
      const notes = await import('../shared/overlays/floating-notes.module');
      if (this.destroyRef.destroyed) {
        return;
      }

      this.notesModuleRef = createNgModule(
        notes.FloatingNotesModule,
        this.environmentInjector,
      );

      this.viewContainerRef.createComponent(notes.FloatingNotesWidgetComponent, {
        environmentInjector: this.notesModuleRef.injector,
      });
    } catch (error) {
      this.notesModuleRef?.destroy();
      this.notesModuleRef = null;
      this.lastLoadError = 'notes';
      this.loadError.set(true);
      console.error('Error loading optional floating notes', error);
    }
  }

  private async loadSidebarUiInternal(): Promise<void> {
    try {
      const sidebar = await import('../shared/overlays/collaboration-shell.module');
      if (this.destroyRef.destroyed) {
        return;
      }

      this.sidebarModuleRef = createNgModule(
        sidebar.CollaborationShellModule,
        this.environmentInjector,
      );

      this.sidebarComponentRef = this.viewContainerRef.createComponent(sidebar.SidebarContainerComponent, {
        environmentInjector: this.sidebarModuleRef.injector,
      });
    } catch (error) {
      this.sidebarModuleRef?.destroy();
      this.sidebarModuleRef = null;
      this.lastLoadError = 'sidebar';
      this.loadError.set(true);
      console.error('Error loading optional collaboration sidebars', error);
    }
  }

}
